import type { AxiosInstance } from 'axios';
import { unwrap, unwrapItems, type ObjectResponse } from '../core/envelope';

/**
 * `ProductReview` — customer product reviews. Verified live
 * 2026-05-14 against `api.knottique.kristysoftware.com`. Three
 * endpoints:
 *
 *   - `POST /auth/me/reviews` — submit/upsert one review per
 *     `(customerId, productId)`. JWT-scoped.
 *   - `POST /Public/products/{productId}/reviews` — anonymous read,
 *     returns only `isApproved=1` rows.
 *   - `POST /auth/me/reviews/Delete` — pull a review the customer
 *     wrote.
 */
export interface ProductReview {
  productReviewId?: string;
  tenantId?: string;
  productId: string;
  /** Server-resolved from JWT. Never set client-side. */
  customerId?: string;
  rating: number;
  text?: string;
  /** Server-side moderation flag. Customer can read their own
   *  un-approved review; the public endpoint hides un-approved rows. */
  isApproved?: boolean;
  /** Server-joined display name when reading via `/Public/.../reviews`. */
  authorName?: string;
  createdAt?: string;
}

export interface ProductReviewService {
  /**
   * Anonymous read of approved reviews for a product. Used by the
   * PDP's `<CustomerReviews>` to display the live list.
   */
  forProduct(productId: string): Promise<ProductReview[]>;
  /**
   * Submit / upsert the signed-in customer's review for a product.
   * Backend de-dupes on `(customerId, productId)` so a re-submission
   * edits the existing row.
   */
  submit(body: { productId: string; rating: number; text?: string }): Promise<ProductReview>;
  /**
   * Delete the signed-in customer's own review by id.
   */
  remove(productReviewId: string): Promise<void>;
}

export function createProductReviewService(
  client: AxiosInstance,
): ProductReviewService {
  return {
    forProduct: (productId) =>
      client
        .post<ObjectResponse<ProductReview[] | { items?: ProductReview[] }>>(
          `/Public/products/${encodeURIComponent(productId)}/reviews`,
          {},
        )
        .then((r) => unwrapItems<ProductReview>(r.data)),

    submit: (body) =>
      client
        .post<ObjectResponse<ProductReview>>('/auth/me/reviews', body)
        .then((r) => unwrap(r.data)),

    remove: (productReviewId) =>
      client
        .post<ObjectResponse<null>>('/auth/me/reviews/Delete', {
          ProductReviewId: productReviewId,
        })
        .then((r) => {
          unwrap(r.data, { allowNullData: true });
        }),
  };
}
